import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { getState, patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Tag, TagKind } from 'mbs-ui-shared@models/tag';
import { TagsService } from 'mbs-ui-shared@services/tags.service';
import { EMPTY, pipe } from 'rxjs';
import { filter, mergeMap, switchMap } from 'rxjs/operators';

interface ComputerTagsState {
  entities: Record<number, Tag>;
  ids: number[];
  selected: number | null;
  loading: boolean;
  loaded: boolean;
}

const initialState: ComputerTagsState = {
  entities: {},
  ids: [],
  selected: null,
  loading: false,
  loaded: false
};

function replaceTagsInState(currentEntities: Record<number, Tag>, currentIds: number[], tags: Tag[]): Partial<ComputerTagsState> {
  const tagIds = tags.map((t) => t.id);
  const remainingIds = currentIds.filter((id) => !tagIds.includes(id));
  const remainingEntities: Record<number, Tag> = Object.fromEntries(remainingIds.map((id) => [id, currentEntities[id]]));
  return {
    entities: { ...remainingEntities, ...Object.fromEntries(tags.map((t) => [t.id, t])) },
    ids: [...remainingIds, ...tagIds],
    loading: false,
    loaded: true
  };
}

export const ComputerTagsStore = signalStore(
  { providedIn: 'root' },
  withDevtools('ComputerTagsStore'),
  withState<ComputerTagsState>(initialState),

  withComputed((store) => ({
    all: computed(() =>
      store
        .ids()
        .map((id) => store.entities()[id])
        .filter(Boolean)
    ),
    selectedTag: computed(() => (store.selected() != null ? store.entities()[store.selected()] : null)),
    total: computed(() => store.ids().length)
  })),

  withMethods((store) => {
    const tagsService = inject(TagsService);

    return {
      entityById: (id: number) => computed(() => store.entities()[id]),

      loadTagById: rxMethod<{ id: number; force?: boolean; quiet?: boolean }>(
        pipe(
          filter(({ id }) => !!id),
          filter(({ id, force }) => !store.entities()[id] || !!force),
          mergeMap(({ id, quiet }) => {
            !quiet && patchState(store, { loading: true });
            return tagsService.getTagById(id).pipe(
              tapResponse({
                next: (result) => {
                  const { entities, ids } = getState(store);
                  patchState(store, replaceTagsInState(entities, ids, [result]));
                },
                error: () => patchState(store, { loading: false })
              })
            );
          })
        )
      ),

      loadAllTags: rxMethod<{ force?: boolean; quiet?: boolean; calculateCount?: boolean }>(
        pipe(
          filter(({ force }) => !store.loaded() || !!force),
          mergeMap(({ quiet, calculateCount }) => {
            !quiet && patchState(store, { loading: true });
            return tagsService.getTagsByKind(TagKind.Computer, calculateCount ?? true).pipe(
              tapResponse({
                next: (tags) => {
                  const { entities, ids } = getState(store);
                  patchState(store, replaceTagsInState(entities, ids, tags));
                },
                error: () => patchState(store, { loading: false })
              })
            );
          })
        )
      ),

      setSelected: rxMethod<number>(
        pipe(
          filter((selected) => selected !== store.selected()),
          switchMap((selectedId) => {
            if (store.entities()[selectedId]) {
              patchState(store, { selected: selectedId });
              return EMPTY;
            }
            return tagsService.getTagById(selectedId).pipe(
              tapResponse({
                next: (result) => {
                  const { entities, ids } = getState(store);
                  patchState(store, { ...replaceTagsInState(entities, ids, [result]), selected: result.id });
                },
                error: () => patchState(store, { loading: false })
              })
            );
          })
        )
      ),

      setTags(tags: Tag[]): void {
        const { entities, ids } = getState(store);
        patchState(store, replaceTagsInState(entities, ids, tags));
      },

      deleteTag(id: number): void {
        const { entities, ids } = getState(store);
        const newIds = ids.filter((i) => i !== id);
        const newEntities = { ...entities };
        delete newEntities[id];
        patchState(store, { entities: newEntities, ids: newIds });
      },

      clear(): void {
        patchState(store, { ...initialState });
      }
    };
  })
);

export type ComputerTagsStoreType = InstanceType<typeof ComputerTagsStore>;
